import React, { useState, useMemo } from "react";
import { Row, Col, Card, CardBody, Spinner, Table, Badge, Progress } from "reactstrap";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import numeral from "numeral";
import "numeral/locales";
import Select from "react-select";

// Existing components
import OverallMetric from "../components/OverallMetric";
import LineGraph from "../components/LineGraph";
import BarGraph from "../components/BarGraph";
import StackedValueGraph from "../components/StackedValueGraph";
import SummaryDonutChart from "../components/SummaryDonutChart";
// Hidden — to be moved to another view
// import PerformanceTable from "../components/Table";
// import BubbleGraph from "../components/BubbleGraph";
import ImageDetectionGrid from "../components/ImageDetectionGrid";
import TrackedAccountPosts from "../components/TrackedAccountPosts";
import InfoTooltip from "../components/InfoTooltip";
// Hidden — to be moved to another view
// import BestSocialMediaPostBubbles from "../components/BestSocialMediaPostBubbles";
// import PerformingSocialMediaPosts from "../components/PerformingSocialMediaPosts";
// import PerformingSocialMediaMentions from "../components/PerformingSocialMediaMentions";
// import PerformingSocialMediaTagged from "../components/PerformingSocialMediaTagged";
import ExposureDetailModal from "../components/ExposureDetailModal";

// Existing hooks (MySQL-based)
// import { useSponsorPerformanceBubble, useSocialAccounts } from "../query/hooks";
// import useSponsorPerformanceTable from "../query/hooks/useSponsorPerformanceTable";
import useSponsorImageDetectionExample from "../query/hooks/useSponsorImageDetectionExample";
import useSponsorTvDiveded from "../query/hooks/useSponsorTvDiveded";

// PA hooks (ClickHouse — placement analysis only)
import usePARightsholders from "../query/hooks/usePASponsorObjects";
import usePAByPlacementAttributed from "../query/hooks/usePAByPlacementAttributed";
import usePAValueDrivers from "../query/hooks/usePAValueDrivers";
import usePAMediaValue from "../query/hooks/usePAMediaValue";
import usePAEngagementValue from "../query/hooks/usePAEngagementValue";
import usePAMediaValueTimeline from "../query/hooks/usePAMediaValueTimeline";
import usePAEngagementValueTimeline from "../query/hooks/usePAEngagementValueTimeline";
import usePATvByChannel from "../query/hooks/usePATvByChannel";
import usePAReachTimeline from "../query/hooks/usePAReachTimeline";
import usePAExposuresTimeline from "../query/hooks/usePAExposuresTimeline";

numeral.locale("no");

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--background-secondary)',
    borderColor: state.isFocused ? 'var(--accent-color)' : 'var(--border-color)',
    color: 'var(--text-primary)',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(var(--accent-color-rgb), 0.25)' : 'none',
    '&:hover': { borderColor: 'var(--accent-color)' }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--background-primary)',
    border: '1px solid var(--border-color)',
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'var(--accent-color)' : state.isFocused ? 'var(--hover-color)' : 'transparent',
    color: state.isSelected ? 'var(--background-primary)' : 'var(--text-primary)',
  }),
  singleValue: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  input: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  placeholder: (provided) => ({ ...provided, color: 'var(--text-secondary)' }),
  groupHeading: (provided) => ({ ...provided, color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.75rem' }),
};

const PLACEMENT_COLORS = {
  stadium_signage: '#5298C5',
  stadium_led_board: '#379f8b',
  stadium_pitch_board: '#ebbc40',
  athlete_jersey_front: '#972BA0',
  athlete_jersey_arm: '#A01049',
  athlete_jersey_back: '#652847',
  field_surface: '#a09ea8',
  interview_backdrop: '#FF9F40',
  screen_overlay: '#4BC0C0',
  sports_equipment: '#FF6384',
  other: '#C9CBCF',
};

const SPONSOR_OBJECT_COLORS = [
  '#5298C5', '#379f8b', '#ebbc40', '#972BA0', '#A01049',
  '#FF9F40', '#4BC0C0', '#FF6384', '#652847', '#a09ea8',
];

const formatScreenShare = (seconds) => {
  if (!seconds || seconds === 0) return "0s";
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

const PlacementBreakdown = ({ data, totalMediaValue, isLoading, currencyMultiplier = 1 }) => {
  if (isLoading) return <div className="text-center py-4"><Spinner size="sm" /></div>;
  if (!data || data.length === 0) return <p style={{ color: 'var(--text-secondary)' }}>No placement data</p>;

  const convertedData = data.map(d => ({ ...d, convertedValue: Math.round(d.value * currencyMultiplier) }));
  const maxValue = Math.max(...convertedData.map(d => d.convertedValue));
  const convertedTotal = Math.round((totalMediaValue || 0) * currencyMultiplier);

  return (
    <div style={{ paddingRight: '0.75rem' }}>
      {/* Column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 70px 50px', gap: '0 0.75rem', alignItems: 'center', marginBottom: '0.4rem', paddingBottom: '0.4rem', borderBottom: '1px solid var(--border-color)' }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}></span>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Value</span>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Airtime</span>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Share</span>
      </div>
      {convertedTotal > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 70px 50px', gap: '0 0.75rem', alignItems: 'center', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>
          <span style={{ color: 'var(--text-primary)', fontSize: '0.8rem', fontWeight: 600 }}>Total</span>
          <span style={{ color: 'var(--text-primary)', fontSize: '0.8rem', fontWeight: 600, textAlign: 'right' }}>{numeral(convertedTotal).format('0,0')}</span>
          <span></span><span></span>
        </div>
      )}
      {convertedData.map((item, idx) => {
        const isUnattributed = item.sponsor_object_id === 0;
        const label = item.label || (item.placement_category || 'unknown').replace(/_/g, ' ');
        const color = isUnattributed ? '#C9CBCF' : (PLACEMENT_COLORS[item.placement_category] || SPONSOR_OBJECT_COLORS[idx % SPONSOR_OBJECT_COLORS.length]);
        const pct = convertedTotal > 0 ? (item.convertedValue / convertedTotal) * 100 : 0;
        const shareLabel = item.share != null ? `${(item.share * 100).toFixed(1)}%` : '—';
        return (
          <div key={idx} className="mb-2" style={isUnattributed ? { opacity: 0.6, borderTop: '1px dashed var(--border-color)', paddingTop: '0.5rem', marginTop: '0.5rem' } : {}}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 70px 50px', gap: '0 0.75rem', alignItems: 'center', marginBottom: '0.3rem' }}>
              <span style={{ color: 'var(--text-primary)', fontSize: '0.82rem', textTransform: 'capitalize', fontStyle: isUnattributed ? 'italic' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
              <span style={{ color: 'var(--text-primary)', fontSize: '0.82rem', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>{numeral(item.convertedValue).format('0,0')}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatScreenShare(item.duration_seconds)}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{shareLabel}</span>
            </div>
            <Progress value={pct} style={{ height: '4px', backgroundColor: 'var(--background-secondary)' }} barStyle={{ backgroundColor: color }} />
          </div>
        );
      })}
    </div>
  );
};

const KpiCard = ({ title, value, format: fmt = "0,0", suffix = "", isLoading, customValue }) => (
  <Card className="mini-stat bg-card h-100">
    <CardBody>
      <h5 className="font-size-16 text-uppercase mt-0 text-white-50 engagement-average">{title}</h5>
      {isLoading ? (
        <Spinner size="sm" />
      ) : (
        <h4 className="font-weight-medium font-size-24" style={{ color: 'var(--text-primary)' }}>
          {customValue ?? <>{numeral(value).format(fmt)}{suffix && ` ${suffix}`}</>}
        </h4>
      )}
    </CardBody>
  </Card>
);

const DashboardSponsorPA = ({ selectedCurrency, tenant }) => {
  const { t } = useTranslation();

  // PA selection state
  const [rightsholderId, setRightsholderId] = useState(0);
  const [parentTenantId, setParentTenantId] = useState(null);
  const [sponsorObjectId, setSponsorObjectId] = useState(0);
  const [sponsorObjectIds, setSponsorObjectIds] = useState([]);
  const [vdGroupBy, setVdGroupBy] = useState('placement');
  const [exposureModal, setExposureModal] = useState({ open: false, sponsorObjectId: null, videoId: null, groupLabel: null });

  // PA filter params for ClickHouse-only components (Value by Placement, Detailed Exposures)
  const paFilterParams = useMemo(() => ({
    rightsholderId,
    parentTenantId,
    sponsorObjectId: rightsholderId > 0 ? 0 : sponsorObjectId,
  }), [rightsholderId, parentTenantId, sponsorObjectId]);

  // Headline value = Media Value + Engagement Value (the two donuts below)
  const mediaValue = usePAMediaValue({ rightsholderId, parentTenantId });
  const engagementValue = usePAEngagementValue({ rightsholderId, parentTenantId });

  // Existing hooks (MySQL-based)
  const imageDetection = useSponsorImageDetectionExample(sponsorObjectId);
  // const { hasActiveSocialAccounts } = useSocialAccounts();

  // PA hooks (ClickHouse — placement analysis only)
  const rightsholders = usePARightsholders();
  const byPlacement = usePAByPlacementAttributed(paFilterParams);
  const valueDrivers = usePAValueDrivers({ ...paFilterParams, groupBy: vdGroupBy });

  // Build dropdown options
  const selectOptions = useMemo(() => {
    if (!rightsholders.data || rightsholders.data.length === 0) return [];

    const portfolio = { value: 'portfolio', label: t("Portfolio"), sponsorObjectId: 0 };

    const formatLeagueName = (name) => {
      if (name === 'Eliteserien') return t("Eliteserien Clubs");
      if (name === 'Toppserien') return t("Toppserien Clubs");
      if (name === 'OBOS-ligaen') return t("OBOS-ligaen Clubs");
      return name;
    };

    const groups = {};
    const ungrouped = [];
    rightsholders.data.forEach(rh => {
      const option = {
        value: `rh:${rh.id}`,
        label: rh.name,
        rightsholderId: rh.id,
        sponsorObjectIds: rh.sponsor_object_ids || [],
      };
      if (rh.parent_tenant_name) {
        if (!groups[rh.parent_tenant_id]) {
          groups[rh.parent_tenant_id] = {
            label: formatLeagueName(rh.parent_tenant_name),
            parentId: rh.parent_tenant_id,
            options: [],
          };
        }
        groups[rh.parent_tenant_id].options.push(option);
      } else {
        ungrouped.push(option);
      }
    });

    const groupedOptions = Object.values(groups).map(g => {
      const allSoIds = g.options.flatMap(o => o.sponsorObjectIds || []);
      return {
        label: g.label,
        options: [
          { value: `parent:${g.parentId}`, label: `${t("All")} ${g.label}`, parentTenantId: g.parentId, sponsorObjectId: 0, sponsorObjectIds: allSoIds },
          ...g.options,
        ],
      };
    });

    return [
      { label: t("Overview"), options: [portfolio] },
      ...groupedOptions,
      ...(ungrouped.length > 0 ? [{ label: t("Other"), options: ungrouped }] : []),
    ];
  }, [rightsholders.data, t]);

  const handleSelectionChange = (opt) => {
    if (!opt || opt.value === 'portfolio') {
      setRightsholderId(0);
      setParentTenantId(null);
      setSponsorObjectId(0);
      setSponsorObjectIds([]);
    } else if (opt.parentTenantId) {
      setRightsholderId(0);
      setParentTenantId(opt.parentTenantId);
      setSponsorObjectId(0);
      setSponsorObjectIds(opt.sponsorObjectIds || []);
    } else if (opt.rightsholderId) {
      setRightsholderId(opt.rightsholderId);
      setParentTenantId(null);
      setSponsorObjectId(0);
      setSponsorObjectIds(opt.sponsorObjectIds || []);
    }
  };

  const currentSelectValue = useMemo(() => {
    if (parentTenantId) {
      for (const group of selectOptions) {
        if (group.options) {
          const found = group.options.find(o => o.value === `parent:${parentTenantId}`);
          if (found) return found;
        }
      }
    }
    if (rightsholderId > 0) {
      for (const group of selectOptions) {
        if (group.options) {
          const found = group.options.find(o => o.value === `rh:${rightsholderId}`);
          if (found) return found;
        }
      }
    }
    return { value: 'portfolio', label: t("Portfolio") };
  }, [rightsholderId, parentTenantId, selectOptions, t]);

  return (
    <>
      <div className="container-fluid mt-3">
        {/* PA Dropdown */}
        <Row className="mb-4">
          <Col md={4}>
            <Select
              options={selectOptions}
              value={currentSelectValue}
              onChange={handleSelectionChange}
              styles={customSelectStyles}
              isSearchable
              placeholder="Select..."
            />
          </Col>
        </Row>

        {/* KPI Cards */}
        <Row className="mb-4">
          <Col xl={3} md={6} className="mb-4 mb-xl-0">
            <KpiCard
              title={t("Sponsorverdi")}
              value={Math.round(((mediaValue.data?.total || 0) + (engagementValue.data?.total || 0)) * selectedCurrency.value)}
              suffix={selectedCurrency.currency}
              isLoading={mediaValue.isLoading || engagementValue.isLoading}
            />
          </Col>
          <Col xl={3} md={6} className="mb-4 mb-xl-0">
            <OverallMetric followCalender metric="sponsor-exposures-pa" params={paFilterParams} />
          </Col>
          <Col xl={3} md={6} className="mb-4 mb-xl-0">
            <OverallMetric followCalender metric="sponsor-reach-pa" params={paFilterParams} append="" />
          </Col>
          <Col xl={3} md={6} className="mb-4 mb-xl-0">
            <OverallMetric followCalender metric="sponsor-time-pa" params={paFilterParams} />
          </Col>
        </Row>

        {/* Media Value + Engagement Value */}
        <Row className="mb-4" style={{ height: 400 }}>
          <Col sm={12} md={6} className="mb-4 mb-xl-0 h-100">
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody style={{ paddingBottom: '1.5rem' }}>
                <h4 className="card-title mb-4">
                  {t("media_value_title")}
                  <InfoTooltip tooltip={t("media_value_tooltip")} />
                </h4>
                <SummaryDonutChart useQuery={usePAMediaValue} queryParams={{ rightsholderId, parentTenantId }} />
              </CardBody>
            </Card>
          </Col>
          <Col sm={12} md={6} className="mb-4 mb-xl-0 h-100">
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody style={{ paddingBottom: '1.5rem' }}>
                <h4 className="card-title mb-4">
                  {t("engagement_value_title")}
                  <InfoTooltip tooltip={t("engagement_value_tooltip")} />
                </h4>
                <SummaryDonutChart useQuery={usePAEngagementValue} queryParams={{ rightsholderId, parentTenantId }} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Platform Performance — hidden, to be moved to another view
        {sponsorObjectId === 0 && !rightsholderId && !parentTenantId && (
          <Row className="mb-4">
            <Col sm={12} md={6} className="mb-4 mb-xl-0">
              <Card className="mini-stat bg-card text-white h-100">
                <CardBody>
                  <h4 className="card-title mb-4">{t("Platform Performance")}</h4>
                  <PerformanceTable useQuery={useSponsorPerformanceTable} params={[sponsorObjectId]} />
                </CardBody>
              </Card>
            </Col>
            <Col sm={12} md={6} className="mb-4 mb-xl-0">
              <Card className="mini-stat bg-card text-white">
                <CardBody>
                  <h4 className="card-title mb-4">{t("Platform Performance Index")}</h4>
                  <BubbleGraph
                    useQuery={useSponsorPerformanceBubble}
                    params={[sponsorObjectId]}
                    xLabel="Engagements"
                    yLabel="Impressions"
                    zLabel="Value"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
        */}

        {/* Value by Placement (PA) */}
        <Row className="mb-4">
          <Col sm={12}>
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody>
                <h4 className="card-title mb-4">
                  {t("media_value_by_placement_title")}
                  {byPlacement.isFetching && <Spinner color="primary" size="sm" className="ml-2" />}
                </h4>
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  <PlacementBreakdown data={byPlacement.data?.data} totalMediaValue={byPlacement.data?.total_media_value} isLoading={byPlacement.isLoading} currencyMultiplier={selectedCurrency.value} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Value charts */}
        <Row className="mb-4">
          <Col sm={12} md={6} className="mb-4 mb-md-0">
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody>
                <h4 className="card-title mb-4">{t("media_value_per_channel_title")} ({selectedCurrency.currency})</h4>
                <StackedValueGraph useGraphQuery={usePAMediaValueTimeline} queryParams={[rightsholderId, parentTenantId]} defaultActiveTab={1} />
              </CardBody>
            </Card>
          </Col>
          <Col sm={12} md={6} className="mb-4 mb-md-0">
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody>
                <h4 className="card-title mb-4">{t("engagement_value_per_channel_title")} ({selectedCurrency.currency})</h4>
                <StackedValueGraph useGraphQuery={usePAEngagementValueTimeline} queryParams={[rightsholderId, parentTenantId]} defaultActiveTab={1} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Reach per channel + TV value per channel */}
        <Row className="mb-4">
          <Col sm={12} md={6} className="mb-4 mb-md-0">
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody>
                <h4 className="card-title mb-4">{t("Visninger per kanal")}</h4>
                <LineGraph useGraphQuery={usePAReachTimeline} queryParams={[paFilterParams.sponsorObjectId, rightsholderId, parentTenantId]} applyCurrencyConversion={false} />
              </CardBody>
            </Card>
          </Col>
          <Col sm={12} md={6} className="mb-4 mb-md-0">
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody>
                <h4 className="card-title mb-4">{t("Sponsorverdi per TV-kanal")} ({selectedCurrency.currency})</h4>
                <LineGraph useGraphQuery={usePATvByChannel} queryParams={[paFilterParams.sponsorObjectId, rightsholderId, parentTenantId]} defaultActiveTab={0} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Exposures + TV viewers */}
        <Row className="mb-4">
          <Col sm={12} md={6} className="mb-4 mb-md-0">
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody>
                <h4 className="card-title mb-4">{t("TV_Eksponeringer")}</h4>
                <LineGraph useGraphQuery={usePAExposuresTimeline} queryParams={[paFilterParams.sponsorObjectId, rightsholderId, parentTenantId]} applyCurrencyConversion={false} defaultActiveTab={1} />
              </CardBody>
            </Card>
          </Col>
          <Col sm={12} md={6} className="mb-4 mb-md-0">
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody>
                <h4 className="card-title mb-4">{t("TV seere")}</h4>
                <BarGraph useGraphQuery={useSponsorTvDiveded} queryParams={[rightsholderId]} scrollbar direction="horizontal" />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Detailed Exposures (PA) */}
        <Row className="mb-4">
          <Col sm={12}>
            <Card className="mini-stat bg-card text-white">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">
                    {t("Detailed Exposures")}
                    {valueDrivers.isFetching && <Spinner color="primary" size="sm" className="ml-2" />}
                  </h4>
                  <div className="btn-group btn-group-sm">
                    {[
                      { key: 'event', label: t('By Event') },
                      { key: 'placement', label: t('By Placement') },
                      { key: 'date', label: t('By Date') },
                    ].map(tab => (
                      <button
                        key={tab.key}
                        className={`btn ${vdGroupBy === tab.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setVdGroupBy(tab.key)}
                        style={vdGroupBy !== tab.key ? { color: 'var(--text-secondary)', borderColor: 'var(--border-color)' } : {}}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
                {valueDrivers.isLoading ? (
                  <div className="text-center py-4"><Spinner size="sm" /></div>
                ) : !valueDrivers.data?.groups?.length ? (
                  <p style={{ color: 'var(--text-secondary)' }}>{t("No data available")}</p>
                ) : (
                  <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                    {valueDrivers.data.groups.map((group, gIdx) => {
                      // Group rows by rightsholder for "By Event" view
                      const isEventView = vdGroupBy === 'event';
                      const mappedRows = group.rows.filter(r => r.sponsor_object_id !== 0);
                      const unmappedRows = group.rows.filter(r => r.sponsor_object_id === 0);

                      // Build rightsholder groups
                      const rhGroups = {};
                      if (isEventView) {
                        mappedRows.forEach(row => {
                          const rhName = row.rightsholder_name || 'Other';
                          if (!rhGroups[rhName]) {
                            rhGroups[rhName] = { name: rhName, rows: [], totalValue: 0, totalDuration: 0 };
                          }
                          rhGroups[rhName].rows.push(row);
                          rhGroups[rhName].totalValue += (row.attributed_value || 0);
                          rhGroups[rhName].totalDuration += (row.duration_seconds || 0);
                        });
                      }
                      const rhGroupList = Object.values(rhGroups).sort((a, b) => b.totalValue - a.totalValue);

                      return (
                      <div key={gIdx} className={gIdx > 0 ? 'mt-4' : ''}>
                        <div className="d-flex justify-content-between align-items-center mb-2" style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                          <h6 className="mb-0" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{group.label}</h6>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600 }}>
                            {numeral(Math.round((group.total_attributed_value || group.total_value) * selectedCurrency.value)).format('0,0')} {selectedCurrency.currency}
                          </span>
                        </div>
                        <Table responsive hover size="sm" className="align-middle mb-0" style={{ tableLayout: 'fixed' }}>
                          <colgroup>
                            <col style={{ width: isEventView ? '30%' : '40%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '14%' }} />
                            {isEventView && <col style={{ width: '6%' }} />}
                          </colgroup>
                          <thead>
                            <tr>
                              <th style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 500 }}>{t("Sponsor Object")}</th>
                              <th style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 500, textAlign: 'right' }}>{t("Reach")}</th>
                              <th style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 500, textAlign: 'right' }}>{t("Duration")}</th>
                              <th style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 500, textAlign: 'right' }}>{t("Share")}</th>
                              <th style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 500, textAlign: 'right' }}>{t("Eksponeringer")}</th>
                              <th style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 500, textAlign: 'right' }}>{t("Value")}</th>
                              {isEventView && <th></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {isEventView ? (
                              <>
                                {rhGroupList.map((rh, rhIdx) => (
                                  <React.Fragment key={`rh-${rhIdx}`}>
                                    <tr style={{ backgroundColor: 'var(--hover-color)' }}>
                                      <td colSpan={2} style={{ color: 'var(--text-primary)', fontSize: '0.8rem', fontWeight: 600 }}>{rh.name}</td>
                                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right', fontWeight: 600 }}>{formatScreenShare(rh.totalDuration)}</td>
                                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right', fontWeight: 600 }}>
                                        {group.total_duration_seconds > 0 ? `${(rh.totalDuration / group.total_duration_seconds * 100).toFixed(1)}%` : ''}
                                      </td>
                                      <td></td>
                                      <td style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'right', fontWeight: 600 }}>
                                        {numeral(Math.round(rh.totalValue * selectedCurrency.value)).format('0,0')} {selectedCurrency.currency}
                                      </td>
                                      <td></td>
                                    </tr>
                                    {rh.rows.map((row, rIdx) => (
                                      <tr key={`row-${rhIdx}-${rIdx}`}>
                                        <td style={{ color: 'var(--text-primary)', fontSize: '0.8rem', paddingLeft: '1.5rem' }}>{row.sponsor_object_name || '-'}</td>
                                        <td style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'right' }}>{numeral(row.reach).format('0,0')}</td>
                                        <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right' }}>{formatScreenShare(row.duration_seconds)}</td>
                                        <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right' }}>{row.duration_share != null ? `${(row.duration_share * 100).toFixed(1)}%` : ''}</td>
                                        <td style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'right' }}>{numeral(row.exposures).format('0,0')}</td>
                                        <td style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'right', fontWeight: 600 }}>
                                          {numeral(Math.round((row.attributed_value || row.value) * selectedCurrency.value)).format('0,0')} {selectedCurrency.currency}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                          <button
                                            className="btn btn-sm btn-outline-primary"
                                            style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }}
                                            onClick={() => setExposureModal({
                                              open: true,
                                              sponsorObjectId: row.sponsor_object_id,
                                              videoId: group.key,
                                              groupLabel: group.label,
                                            })}
                                          >
                                            {t("View")}
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                ))}
                                {unmappedRows.length > 0 && unmappedRows.map((row, rIdx) => (
                                  <tr key={`unattr-${rIdx}`} style={{ opacity: 0.6, borderTop: rIdx === 0 ? '1px dashed var(--border-color)' : 'none' }}>
                                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontStyle: 'italic' }}>{t("Unattributed")}</td>
                                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right' }}>{numeral(row.reach).format('0,0')}</td>
                                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right' }}>{formatScreenShare(row.duration_seconds)}</td>
                                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right' }}>{row.duration_share != null ? `${(row.duration_share * 100).toFixed(1)}%` : ''}</td>
                                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right' }}>{numeral(row.exposures).format('0,0')}</td>
                                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right', fontWeight: 600 }}>
                                      {numeral(Math.round((row.attributed_value || row.value) * selectedCurrency.value)).format('0,0')} {selectedCurrency.currency}
                                    </td>
                                    <td></td>
                                  </tr>
                                ))}
                              </>
                            ) : (
                              group.rows.map((row, rIdx) => (
                                <tr key={rIdx} style={row.sponsor_object_id === 0 ? { opacity: 0.6 } : {}}>
                                  <td style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }}>{row.sponsor_object_name || '-'}</td>
                                  <td style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'right' }}>{numeral(row.reach).format('0,0')}</td>
                                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right' }}>{formatScreenShare(row.duration_seconds)}</td>
                                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textAlign: 'right' }}>{row.duration_share != null ? `${(row.duration_share * 100).toFixed(1)}%` : ''}</td>
                                  <td style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'right' }}>{numeral(row.exposures).format('0,0')}</td>
                                  <td style={{ color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'right', fontWeight: 600 }}>
                                    {numeral(Math.round((row.attributed_value || row.value) * selectedCurrency.value)).format('0,0')} {selectedCurrency.currency}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <TrackedAccountPosts rightsholderId={rightsholderId} parentTenantId={parentTenantId} />

        {/* Detection examples */}
        <Row className="mb-4">
          <Col sm={12}>
            <Card className="mini-stat bg-card text-white h-100">
              <CardBody>
                <h4 className="card-title mb-4">{t('Ex. brand detections')}</h4>
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  <ImageDetectionGrid data={imageDetection.data} isLoading={imageDetection.isLoading} isRefetching={imageDetection.isRefetching} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Social media sections — hidden, to be moved to another view
        {hasActiveSocialAccounts && (
          <>
            <Row className="mb-4">
              <Col md={12}><BestSocialMediaPostBubbles /></Col>
            </Row>
            <Row>
              <Col md={12} className="mb-4"><PerformingSocialMediaPosts type="BESTE" /></Col>
            </Row>
            <Row>
              <Col md={12} className="mb-4"><PerformingSocialMediaPosts type="SVAKESTE" /></Col>
            </Row>
            <Row>
              <Col md={12} className="mb-4"><PerformingSocialMediaMentions /></Col>
            </Row>
            <Row>
              <Col md={12} className="mb-4"><PerformingSocialMediaTagged /></Col>
            </Row>
          </>
        )}
        */}
      </div>

      <ExposureDetailModal
        isOpen={exposureModal.open}
        toggle={() => setExposureModal({ open: false, sponsorObjectId: null, videoId: null, groupLabel: null })}
        sponsorObjectId={exposureModal.sponsorObjectId}
        videoId={exposureModal.videoId}
        groupLabel={exposureModal.groupLabel}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  selectedCurrency: state.currency.selectedCurrency,
  tenant: state.auth.tenant,
});

export default connect(mapStateToProps)(DashboardSponsorPA);
